import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import get from 'lodash/get';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  ComplianceApiComplianceCensusGetRequest,
  ComplianceApiComplianceCensusIdPutRequest,
  CreateCensusDto,
  RecalculateHPPDRequest,
  useApi,
} from '../index';

export enum CensusCacheKeys {
  Census = 'census',
  EditPermission = 'censusEditPermission',
}

export const useCensusGetApi = (
  params: ComplianceApiComplianceCensusGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [CensusCacheKeys.Census, params],
    queryFn: () =>
      api.complianceApi
        .complianceCensusGet(params)
        .then((response) => response?.data?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(params.startDate) && Boolean(params.endDate),
  });
};

export type CensusResponseType = NonNullable<
  UnwrapArray<NonNullable<ReturnType<typeof useCensusGetApi>['data']>>
>;

const updateCensus =
  (api: Api) =>
  (
    complianceApiComplianceCensusIdPutRequest: ComplianceApiComplianceCensusIdPutRequest
  ) =>
    api.complianceApi.complianceCensusIdPut(
      complianceApiComplianceCensusIdPutRequest
    );

export const useUpdateCensus = () => {
  const api = useApi();
  const { notifyError, notifySuccess } = useSnackBar();
  const queryClient = useQueryClient();

  return useMutation(updateCensus(api), {
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries([CensusCacheKeys.Census]);
      const message = get(response, 'data.message', '');
      notifySuccess(message);
    },
  });
};

const createCensus = (api: Api) => (createCensusDto: CreateCensusDto) =>
  api.complianceApi.complianceCensusPost({ createCensusDto });

export const useCreateCensus = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();

  return useMutation(createCensus(api), {
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CensusCacheKeys.Census]);
    },
  });
};

const recalculateHPPD =
  (api: Api) => (recalculateHPPDRequest: RecalculateHPPDRequest) => {
    return api.complianceApi.complianceRecalculateHppdPut({
      recalculateHPPDRequest,
    });
  };

export const useRecalculateHPPD = () => {
  const api = useApi();
  const { notifyError, notifySuccess } = useSnackBar();
  const queryClient = useQueryClient();

  return useMutation(recalculateHPPD(api), {
    onError: async (error) => {
      const message = get(error, 'response.data.message', '');
      notifyError(message);
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries([CensusCacheKeys.Census]);
      const message = get(response, 'data.message', '');
      notifySuccess(message);
    },
  });
};

export const useCensusEditPermission = () => {
  const api = useApi();
  return useQuery({
    queryKey: [CensusCacheKeys.EditPermission],
    queryFn: () =>
      api.complianceApi
        .complianceCensusEditPermissionGet()
        .then((response) => response?.data?.data ?? null),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
